/**
 * MenuBar Custom Element
 * Handles sticky menu functionality, smooth scrolling, and active menu item highlighting
 */
class MenuBar extends HTMLElement {
  constructor() {
    super();
    
    // Properties
    this.header = null;
    this.isSticky = false;
    this.initialTop = 0;
    this.headerHeight = 0;
    this.menuLinks = [];
    this.sections = [];
    this.scrollHandler = null;
    this.resizeHandler = null;
  }

  /**
   * Initialize the menu bar when connected to DOM
   */
  connectedCallback() {
    // Find the sticky header element
    this.header = document.querySelector('.sticky-header');
    
    // Validate and hide invalid menu items first
    this.validateAndHideMenuItems();
    
    // Check if there are any valid menu items, if not hide the entire menu
    if (this.menuLinks.length === 0) {
      this.style.display = 'none';
      return;
    }
    
    // Check if this menu should be sticky
    this.isSticky = this.classList.contains('sticky-section');
    
    if (this.isSticky) {
      this.initStickyMenu();
    }
    
    // Initialize smooth scrolling
    this.initSmoothScroll();
    
    // Initialize active menu item tracking
    this.initActiveMenuTracking();
  }

  /**
   * Clean up event listeners when disconnected
   */
  disconnectedCallback() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Initialize sticky menu functionality
   */
  initStickyMenu() {
    // Store initial position
    this.initialTop = this.getBoundingClientRect().top + window.pageYOffset;
    
    // Calculate header height and set top position
    this.updateHeaderHeight();
    
    // Create bound handlers
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = this.handleResize.bind(this);
    
    // Add event listeners
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });
    
    // Check initial scroll position
    this.handleScroll();
  }

  /**
   * Update header height and apply to CSS variable
   * Reduces gap by subtracting 2px from header height
   */
  updateHeaderHeight() {
    if (this.header) {
      // Subtract 2px to reduce gap between header and menu
      this.headerHeight = Math.max(0, this.header.offsetHeight - 2);
    } else {
      this.headerHeight = 0;
    }
    
    // Apply header height as top position via CSS variable
    this.style.setProperty('--sticky-top-position', `${this.headerHeight}px`);
  }

  /**
   * Handle scroll event for sticky menu
   */
  handleScroll() {
    if (!this.isSticky) return;
    
    const scrollY = window.pageYOffset || window.scrollY;
    const shouldBeSticky = scrollY >= this.initialTop;
    
    if (shouldBeSticky && !this.classList.contains('is-sticky')) {
      this.classList.add('is-sticky');
    } else if (!shouldBeSticky && this.classList.contains('is-sticky')) {
      this.classList.remove('is-sticky');
    }
  }

  /**
   * Handle resize event to recalculate header height
   */
  handleResize() {
    this.updateHeaderHeight();
    this.initialTop = this.getBoundingClientRect().top + window.pageYOffset;
  }

  /**
   * Validate menu items and hide those with blank IDs or no matching sections
   */
  validateAndHideMenuItems() {
    // Reset menu links array
    this.menuLinks = [];
    
    // Get all menu links with data-smooth-scroll attribute
    const allMenuLinks = this.querySelectorAll('a[data-smooth-scroll]');
    const menuList = this.querySelector('.menu-list');
    
    allMenuLinks.forEach(link => {
      const targetId = link.getAttribute('href');
      let shouldHide = false;
      
      // Check if menu_id is blank or empty
      if (!targetId || !targetId.startsWith('#') || targetId === '#') {
        shouldHide = true;
      } else {
        // Remove # from the ID
        const sectionId = targetId.substring(1);
        
        // Check if sectionId is blank/empty after removing #
        if (!sectionId || sectionId.trim() === '') {
          shouldHide = true;
        } else {
          // Check if there's a matching section with this ID
          const targetSection = document.getElementById(sectionId) || 
                               document.querySelector(`[id="${sectionId}"]`);
          
          if (!targetSection) {
            shouldHide = true;
          }
        }
      }
      
      // Hide the menu item if it's invalid
      if (shouldHide) {
        const menuItem = link.closest('.menu-item');
        if (menuItem) {
          menuItem.style.display = 'none';
        }
      } else {
        // Store valid links
        this.menuLinks.push(link);
      }
    });
    
    // Check if menu list is empty after hiding invalid items
    if (menuList) {
      const visibleItems = Array.from(menuList.querySelectorAll('.menu-item')).filter(
        item => item.style.display !== 'none'
      );
      
      if (visibleItems.length === 0) {
        menuList.style.display = 'none';
      }
    }
  }

  /**
   * Initialize smooth scrolling for menu links
   */
  initSmoothScroll() {
    // Only process valid menu links (already filtered in validateAndHideMenuItems)
    if (this.menuLinks.length === 0) return;
    
    // Add click event listeners to each valid link
    this.menuLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        const targetId = link.getAttribute('href');
        if (!targetId || !targetId.startsWith('#')) return;
        
        // Remove # from the ID
        const sectionId = targetId.substring(1);
        
        // Find the target section
        const targetSection = document.getElementById(sectionId) || 
                             document.querySelector(`[id="${sectionId}"]`);
        
        if (targetSection) {
          // Calculate scroll position accounting for sticky menu and header
          const headerOffset = this.header ? this.header.offsetHeight : 0;
          const menuOffset = this.classList.contains('is-sticky') ? this.offsetHeight : 0;
          const totalOffset = headerOffset + menuOffset;
          
          const elementPosition = targetSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - totalOffset;
          
          // Smooth scroll to target
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Update active menu item
          this.updateActiveMenuItem(link);
        }
      });
    });
  }

  /**
   * Initialize active menu item tracking based on scroll position
   */
  initActiveMenuTracking() {
    // Only track if we have valid menu links
    if (this.menuLinks.length === 0) return;
    
    // Get all sections that correspond to menu items
    this.menuLinks.forEach(link => {
      const targetId = link.getAttribute('href');
      if (targetId && targetId.startsWith('#')) {
        const sectionId = targetId.substring(1);
        const section = document.getElementById(sectionId) || 
                       document.querySelector(`[id="${sectionId}"]`);
        if (section) {
          this.sections.push({ link, section });
        }
      }
    });
    
    // Add scroll listener to update active menu item
    if (this.sections.length > 0) {
      const scrollTrackingHandler = this.handleScrollTracking.bind(this);
      window.addEventListener('scroll', scrollTrackingHandler, { passive: true });
    }
  }

  /**
   * Handle scroll tracking to update active menu item
   */
  handleScrollTracking() {
    const scrollY = window.pageYOffset || window.scrollY;
    const headerOffset = this.header ? this.header.offsetHeight : 0;
    const menuOffset = this.classList.contains('is-sticky') ? this.offsetHeight : 0;
    const totalOffset = headerOffset + menuOffset;
    
    // Find which section is currently in view
    let activeLink = null;
    
    for (let i = this.sections.length - 1; i >= 0; i--) {
      const { link, section } = this.sections[i];
      const sectionTop = section.getBoundingClientRect().top + scrollY;
      
      if (scrollY + totalOffset >= sectionTop - 100) {
        activeLink = link;
        break;
      }
    }
    
    // Update active state
    if (activeLink) {
      this.updateActiveMenuItem(activeLink);
    }
  }

  /**
   * Update active menu item
   * @param {HTMLElement} activeLink - The link to mark as active
   */
  updateActiveMenuItem(activeLink) {
    // Remove active class from all links
    this.menuLinks.forEach(link => {
      link.classList.remove('active');
    });
    
    // Add active class to the clicked/viewed link
    if (activeLink) {
      activeLink.classList.add('active');
    }
  }
}

// Register the custom element
customElements.define('menu-bar', MenuBar);